import React from 'react';
import { ArrowLeft, Search, MessageSquare, Mic, ShoppingBag, MessageCircle, Users, TrendingDown, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
interface Tool {
  name: string;
  description: string;
  url: string;
  badge: string;
  icon: string;
}
const AI_CHATBOTS: Tool[] = [{
  name: 'ChatGPT',
  description: 'Multi-category shopping & research buddy. Ask anything in natural language: "Best 55\'\' TV for bright room under $800", "Help me pick a winter coat for Berlin." New shopping features with Instant Checkout from Etsy/Shopify merchants.',
  url: 'https://chatgpt.com',
  badge: 'Free to try',
  icon: '🤖'
}, {
  name: 'Perplexity AI',
  description: 'AI answer engine that now shops with you. Conversational search engine that cites sources with shopping mode: ask questions like "I need a minimalist white sneaker, under $120, not Nike" and it pulls real-time product data with in-app shopping + PayPal Instant Buy.',
  url: 'https://www.perplexity.ai',
  badge: 'Free',
  icon: '🔍'
}, {
  name: 'Google Gemini',
  description: 'AI that finds products & shows where to buy. Brainstorm ("What should I get my coffee-obsessed sister who travels a lot?") and Gemini turns that into actual product suggestions with links and comparison tables.',
  url: 'https://gemini.google.com',
  badge: 'Free',
  icon: '✨'
}, {
  name: 'Le Chat by Mistral',
  description: 'Lightweight, fast research assistant. General multilingual AI chat for product research, comparisons, pros/cons lists, or asking "what should I look for in a gaming monitor vs office monitor."',
  url: 'https://chat.mistral.ai/chat',
  badge: 'Free',
  icon: '💬'
}, {
  name: 'Merlin AI',
  description: 'Chat with any website/page. Open an Amazon/Best Buy/Zara page, then ask things like "Summarize key differences vs this other model" or "Is this good for small hands/bright room/wide feet?" Meta-advisor that sits on top of any site.',
  url: 'https://www.getmerlin.in',
  badge: 'Free',
  icon: '🔮'
}, {
  name: 'TypingMind',
  description: "Personal multi-model AI workspace. Desktop/web app where you connect your own ChatGPT/Claude/Gemini APIs and get a power-user UI for AI chats. Hub to do deep product research, keep chat history, compare models' answers.",
  url: 'https://www.typingmind.com',
  badge: 'Paid',
  icon: '⌨️'
}, {
  name: 'Character AI',
  description: 'Talk to characters for shopping advice. Users can create "expert" characters (e.g. "TV salesman", "sneaker stylist") and chat with them for ideas. More playful, persona-based advisor.',
  url: 'https://character.ai',
  badge: 'Free',
  icon: '🎭'
}];
const VOICE_ASSISTANTS: Tool[] = [{
  name: 'Amazon Alexa',
  description: 'Voice shopping deeply integrated with Amazon. Ask for product recommendations, reviews, and place orders hands-free.',
  url: 'https://www.amazon.com/alexa',
  badge: 'Free with device',
  icon: '🔊'
}, {
  name: 'Google Assistant',
  description: 'Voice queries across Google Shopping with AI that can call stores to check product stock for you.',
  url: 'https://assistant.google.com',
  badge: 'Free',
  icon: '🎙️'
}, {
  name: 'Apple Siri',
  description: 'Voice assistant for product questions and web searches, integrated across Apple devices.',
  url: 'https://www.apple.com/siri/',
  badge: 'Free with Apple',
  icon: '🍎'
}];
const RETAIL_PLATFORMS: Tool[] = [{
  name: 'Amazon Rufus',
  description: 'Virtual shopping assistant in Amazon app. Ask open questions ("good laptop for video editing under $1,000", "TV for bright living room") and Rufus will explain features, compare options, and show you relevant products.',
  url: 'https://www.amazon.com',
  badge: 'Free',
  icon: '📦'
}, {
  name: 'Klarna AI Assistant',
  description: 'Shopping + payments + deal finder. Chat-based shopping assistant that helps you find products at the right price across stores. Browser extension auto-applies coupons and shows better deals at 20,000+ stores.',
  url: 'https://www.klarna.com/us/browser-extension/',
  badge: 'Free',
  icon: '💳'
}, {
  name: 'Best Buy Gift Finder',
  description: 'AI assistant built on Google Vertex AI. Simulates a human-like shopping assistant to help you choose gifts/electronics by asking about the recipient and budget. Summarizes thousands of product reviews.',
  url: 'https://www.bestbuy.com',
  badge: 'Free',
  icon: '💻'
}, {
  name: 'Walmart Sparky',
  description: "AI shopping assistant for occasion-based recommendations and review summaries across Walmart's full catalog.",
  url: 'https://www.walmart.com',
  badge: 'Free',
  icon: '🛒'
}, {
  name: 'Target Gift Finder',
  description: 'AI tool that suggests gift ideas based on age, interests, and occasion through chat-like interface.',
  url: 'https://www.target.com',
  badge: 'Free',
  icon: '🎯'
}, {
  name: "Levi's AI Stylist",
  description: 'Outfitting: when you view an item (e.g. jeans), a "Complete the Look" section shows full outfits. AI stylist chatbot gives personalized outfit recommendations and gets smarter as you browse/buy.',
  url: 'https://www.levi.com',
  badge: 'Free',
  icon: '👖'
}, {
  name: 'Alibaba AI Shopping',
  description: 'Qwen-powered chatbot within Taobao and Tmall apps for finding and buying items across marketplaces.',
  url: 'https://www.alibaba.com',
  badge: 'Free',
  icon: '🛍️'
}];
const MESSAGING_COMMERCE: Tool[] = [{
  name: 'WhatsApp Shopping',
  description: 'AI chatbots and live agents on WhatsApp for personalized product recommendations, catalogs, and instant purchasing.',
  url: 'https://business.whatsapp.com',
  badge: 'Free for users',
  icon: '💬'
}, {
  name: 'WeChat Commerce',
  description: 'In-app shopping conversations pioneered in China. Browse, chat with sellers, and purchase without leaving WeChat.',
  url: 'https://www.wechat.com',
  badge: 'Free',
  icon: '💚'
}];
const HUMAN_EXPERTS: Tool[] = [{
  name: 'Curated',
  description: "Human experts for gear (sports, outdoors, some electronics). Fill in what you need (e.g. skis, bike, golf clubs, espresso machines). You're matched with a human expert who chats with you, recommends specific items, and explains why.",
  url: 'https://www.curated.com',
  badge: 'Free advice',
  icon: '🎿'
}, {
  name: 'Best Buy Expert',
  description: 'Free live chat or video call with tech experts for personalized advice on electronics and appliances.',
  url: 'https://www.bestbuy.com/site/electronics/expert-service/pcmcat1528819595254.c',
  badge: 'Free',
  icon: '👨‍💻'
}, {
  name: 'Nordstrom Digital Styling',
  description: 'Digital Styling: answer a quick style quiz and get curated outfit recommendations emailed to you. Virtual or in-store styling appointments: 1:1 sessions with real stylists for fit & style advice, outfits for events.',
  url: 'https://www.nordstrom.com/browse/services/personal-stylists',
  badge: 'Free service',
  icon: '👗'
}, {
  name: 'Apple Specialist',
  description: 'Chat with Apple Specialists online for personalized Mac, iPhone, and accessory recommendations.',
  url: 'https://www.apple.com/retail/geniusbar/',
  badge: 'Free',
  icon: '🍏'
}];
const PRICE_TRACKING: Tool[] = [{
  name: 'CamelCamelCamel',
  description: 'Amazon price tracker showing 90-day price history and alerts when items drop to your target price.',
  url: 'https://camelcamelcamel.com',
  badge: 'Free',
  icon: '🐫'
}, {
  name: 'PayPal Honey',
  description: 'Browser extension that automatically finds coupon codes and monitors prices across thousands of stores.',
  url: 'https://www.joinhoney.com',
  badge: 'Free',
  icon: '🍯'
}, {
  name: 'Keepa',
  description: 'Amazon price history tracker with detailed charts, price drop alerts, and deal notifications.',
  url: 'https://keepa.com',
  badge: 'Free',
  icon: '📊'
}, {
  name: 'Slickdeals',
  description: 'Community-driven deals platform with AI-powered personalized deal alerts and price tracking.',
  url: 'https://slickdeals.net',
  badge: 'Free',
  icon: '🔥'
}];
function ToolCard({
  tool
}: {
  tool: Tool;
}) {
  return <a href={tool.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 w-80 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md transition-all p-5 group" data-id="element-1279">
      <div className="flex items-start gap-4 mb-3" data-id="element-1280">
        <div className="text-4xl" data-id="element-1281">{tool.icon}</div>
        <div className="flex-1 min-w-0" data-id="element-1282">
          <h3 className="font-bold text-lg text-slate-900 mb-1 group-hover:text-slate-700 flex items-center gap-2" data-id="element-1283">
            {tool.name}
            <ExternalLink size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1284" />
          </h3>
          <span className="inline-block text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 font-semibold" data-id="element-1285">
            {tool.badge}
          </span>
        </div>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed line-clamp-3" data-id="element-1286">
        {tool.description}
      </p>
    </a>;
}
function ToolSection({
  title,
  tools,
  icon
}: {
  title: string;
  tools: Tool[];
  icon: React.ReactNode;
}) {
  return <section className="mb-12" data-id="element-1287">
      <div className="flex items-center gap-3 mb-4" data-id="element-1288">
        <div className="w-10 h-10 rounded-xl bg-slate-100 grid place-items-center text-slate-700" data-id="element-1289">
          {icon}
        </div>
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-1290">{title}</h2>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide" data-id="element-1291">
        {tools.map((tool, index) => <ToolCard key={index} tool={tool} data-id="element-1292" />)}
      </div>
    </section>;
}
export function ResearchPage() {
  return <div className="min-h-screen bg-slate-50" data-id="element-1293">
      {/* Header */}
      <header className="sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-white/70 bg-white/90 border-b border-slate-200" data-id="element-1294">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4" data-id="element-1295">
          <div className="flex items-center justify-between" data-id="element-1296">
            <div className="flex items-center gap-4" data-id="element-1297">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-1298">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4yJ3FscyPfrN1xAm7crLNW/ChatGPT_Image_Dec_2%2C_2025%2C_10_42_07_PM.png" alt="Shopping Expert.ai Logo" className="h-12 object-contain" data-id="element-1299" />
                <span className="text-xl font-bold text-slate-900" data-id="element-1300">
                  AI Shopping Expert
                </span>
              </Link>
            </div>
            <nav className="hidden md:flex items-center gap-6 text-sm" data-id="element-1301">
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-1302">
                All Tools
              </Link>
              <Link to="/research" className="text-slate-900 font-semibold border-b-2 border-slate-900 pb-1" data-id="element-1303">
                Shopping Research
              </Link>
              <a href="mailto:support@shopilots.com" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-1304">
                Submit a Tool
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16 px-4 sm:px-6" data-id="element-1305">
        <div className="mx-auto max-w-7xl" data-id="element-1306">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-300 hover:text-white mb-6 text-sm" data-id="element-1307">
            <ArrowLeft size={16} data-id="element-1308" />
            Back to directory
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-id="element-1309">
            Shopping Research Tools
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl" data-id="element-1310">
            AI chatbots, voice assistants, retail platforms, and human experts
            to guide every purchase decision
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-12" data-id="element-1311">
        <ToolSection title="AI Chatbots" tools={AI_CHATBOTS} icon={<MessageSquare size={20} data-id="element-1313" />} data-id="element-1312" />

        <ToolSection title="Voice Assistants" tools={VOICE_ASSISTANTS} icon={<Mic size={20} data-id="element-1315" />} data-id="element-1314" />

        <ToolSection title="Retail Platform Assistants" tools={RETAIL_PLATFORMS} icon={<ShoppingBag size={20} data-id="element-1317" />} data-id="element-1316" />

        <ToolSection title="Messaging Commerce" tools={MESSAGING_COMMERCE} icon={<MessageCircle size={20} data-id="element-1319" />} data-id="element-1318" />

        <ToolSection title="Human Expert Services" tools={HUMAN_EXPERTS} icon={<Users size={20} data-id="element-1321" />} data-id="element-1320" />

        <ToolSection title="Price Tracking & Deals" tools={PRICE_TRACKING} icon={<TrendingDown size={20} data-id="element-1323" />} data-id="element-1322" />
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-16" data-id="element-1324">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-id="element-1325">
          <p className="text-sm text-slate-500 text-center" data-id="element-1326">
            © 2025 AI Shopping Expert. Some links may be affiliate links. We
            may earn a commission at no extra cost to you.
          </p>
        </div>
      </footer>

      <style data-id="element-1327">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
}